import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useAuth } from './AuthContext';
import { offlineStorage } from '@/lib/offlineStorage';
import { toast as sonnerToast } from 'sonner';

interface Cliente {
  id: string;
  nombre: string;
  direccion: string | null;
  piso?: string | null;
  puerta?: string | null;
  telefono: string | null;
  email?: string | null;
  nif_cif?: string | null;
  nombre_fiscal?: string | null;
  direccion_fiscal?: string | null;
  poblacion?: string | null;
  codigo_postal?: string | null;
  dni_nif?: string | null;
  telefono_facturacion?: string | null;
  email_facturacion?: string | null;
  horario_apertura?: string | null;
  horario_cierre?: string | null;
  tipo_conexion?: string | null;
  numero_linea_comunicacion?: string | null;
  contrasena_cra?: string | null;
  palabra_clave_cra?: string | null;
  numero_abonado?: string | null;
  palabra_coaccion?: string | null;
  personas_contacto?: any;
  contactos_operativos?: any;
  empresa_id?: string;
  activo?: boolean;
  tipo_contacto?: string | null;
}

interface Sistema {
  id: string;
  cliente_id: string;
  tipo: string;
  descripcion: string | null;
  ubicacion: string | null;
  categoria: 'seguridad' | 'pci';
  empresa_id?: string;
  sede_id?: string | null;
  activo?: boolean;
  archivado_at?: string | null;
  archivado_por?: string | null;
}

export interface ContactoCRA {
  nombre: string;
  telefono: string;
  cargo?: string;
  prioridad?: number;
}

export interface Sede {
  id: string;
  cliente_id: string;
  empresa_id: string;
  nombre: string;
  direccion?: string | null;
  codigo_postal?: string | null;
  poblacion?: string | null;
  provincia?: string | null;
  contacto_nombre?: string | null;
  contacto_telefono?: string | null;
  contacto_email?: string | null;
  notas?: string | null;
  activa: boolean;
  // CRA (sede-level)
  numero_abonado?: string | null;
  numero_abonado_incendio?: string | null;
  contrasena_cra?: string | null;
  palabra_clave_cra?: string | null;
  palabra_coaccion?: string | null;
  horario_apertura?: string | null;
  horario_cierre?: string | null;
  contactos_cra?: ContactoCRA[] | null;
  created_at?: string;
  updated_at?: string;
}

interface Elemento {
  id: string;
  sistema_id: string;
  nombre: string;
  tipo: string;
  estado: string;
  observaciones: string | null;
  revisado?: boolean;
  fecha_instalacion?: string;
  tipo_equipo?: string;
  marca?: string;
  modelo?: string;
  vr?: string;
  zona?: string;
  area_particion?: string;
  mac_serie?: string;
  codigo_instalador?: string;
  usuario?: string;
  contrasena?: string;
  anio_fabricacion?: string;
  datos_tecnicos?: any;
}

interface TipoEquipo {
  id: string;
  nombre: string;
  sistema_tipo: string;
  categoria: 'seguridad' | 'pci';
  created_at: string;
}

interface MarcaEquipo {
  id: string;
  nombre: string;
  sistema_tipo: string;
  categoria: 'seguridad' | 'pci';
  created_at: string;
}

interface ModeloEquipo {
  id: string;
  nombre: string;
  marca_id: string;
  sistema_tipo: string;
  tipo_equipo: string | null;
  categoria: 'seguridad' | 'pci';
  created_at: string;
}

interface SecurityContextType {
  clientes: Cliente[];
  sistemas: Sistema[];
  sistemasArchivados: Sistema[];
  elementos: Elemento[];
  tiposEquipo: TipoEquipo[];
  marcasEquipo: MarcaEquipo[];
  modelosEquipo: ModeloEquipo[];
  sedes: Sede[];
  clienteActual: Cliente | null;
  sistemaActual: Sistema | null;
  sedeActual: Sede | null;
  isSyncing: boolean;
  pendingChangesCount: number;
  setClienteActual: (cliente: Cliente | null) => void;
  setSistemaActual: (sistema: Sistema | null) => void;
  setSedeActual: (sede: Sede | null) => void;
  cargarClientes: () => Promise<void>;
  cargarSistemas: () => Promise<void>;
  cargarElementos: () => Promise<void>;
  cargarSedes: () => Promise<void>;
  sincronizarCambiosPendientes: () => Promise<void>;
  sincronizarTiposEquipo: () => Promise<void>;
  sincronizarMarcasEquipo: () => Promise<void>;
  sincronizarModelosEquipo: () => Promise<void>;
  crearCliente: (cliente: Omit<Cliente, 'id'>) => Promise<void>;
  editarCliente: (id: string, cliente: Partial<Cliente>) => Promise<void>;
  eliminarCliente: (id: string) => Promise<void>;
  crearSistema: (sistema: Omit<Sistema, 'id'>) => Promise<void>;
  editarSistema: (id: string, sistema: Partial<Sistema>) => Promise<void>;
  archivarSistema: (id: string) => Promise<void>;
  restaurarSistema: (id: string) => Promise<void>;
  eliminarSistemaDefinitivo: (id: string) => Promise<{ ok: boolean; motivo?: string }>;
  crearElemento: (elemento: Omit<Elemento, 'id'>) => Promise<void>;
  editarElemento: (id: string, elemento: Partial<Elemento>) => Promise<void>;
  eliminarElemento: (id: string) => Promise<void>;
  crearSede: (sede: Omit<Sede, 'id' | 'empresa_id' | 'created_at' | 'updated_at' | 'activa'> & { activa?: boolean }) => Promise<Sede | null>;
  editarSede: (id: string, cambios: Partial<Sede>) => Promise<void>;
  eliminarSede: (id: string) => Promise<void>;
  crearTipoEquipo: (nombre: string, sistemaTipo: string, categoria?: 'seguridad' | 'pci') => Promise<void>;
  crearMarcaEquipo: (nombre: string, sistemaTipo: string, categoria?: 'seguridad' | 'pci') => Promise<void>;
  crearModeloEquipo: (nombre: string, marcaId: string, sistemaTipo: string, tipoEquipo?: string, categoria?: 'seguridad' | 'pci') => Promise<void>;
  editarTipoEquipo: (id: string, cambios: Partial<TipoEquipo>) => Promise<void>;
  eliminarTipoEquipo: (id: string) => Promise<void>;
  editarMarcaEquipo: (id: string, cambios: Partial<MarcaEquipo>) => Promise<void>;
  eliminarMarcaEquipo: (id: string) => Promise<void>;
  editarModeloEquipo: (id: string, cambios: Partial<ModeloEquipo>) => Promise<void>;
  eliminarModeloEquipo: (id: string) => Promise<void>;
  recursosOcultos: { tipos: Set<string>; marcas: Set<string>; modelos: Set<string> };
  ocultarRecurso: (recurso: 'tipo' | 'marca' | 'modelo', id: string) => Promise<void>;
  mostrarRecurso: (recurso: 'tipo' | 'marca' | 'modelo', id: string) => Promise<void>;
  isLoading: boolean;
}

const SecurityContext = createContext<SecurityContextType | undefined>(undefined);

export const useSecurityContext = () => {
  const context = useContext(SecurityContext);
  if (context === undefined) {
    throw new Error('useSecurityContext must be used within a SecurityProvider');
  }
  return context;
};



export const SecurityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { perfil, user } = useAuth();
  const isOnline = useOnlineStatus();
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [sistemasTodos, setSistemas] = useState<Sistema[]>([]);
  const sistemas = React.useMemo(
    () => sistemasTodos.filter(s => s.activo !== false),
    [sistemasTodos]
  );
  const sistemasArchivados = React.useMemo(
    () => sistemasTodos.filter(s => s.activo === false),
    [sistemasTodos]
  );
  const [elementos, setElementos] = useState<Elemento[]>([]);
  const [tiposEquipo, setTiposEquipo] = useState<TipoEquipo[]>([]);
  const [marcasEquipo, setMarcasEquipo] = useState<MarcaEquipo[]>([]);
  const [modelosEquipo, setModelosEquipo] = useState<ModeloEquipo[]>([]);
  const [sedes, setSedes] = useState<Sede[]>([]);
  const [clienteActual, setClienteActual] = useState<Cliente | null>(null);
  const [sistemaActual, setSistemaActual] = useState<Sistema | null>(null);
  const [sedeActual, setSedeActual] = useState<Sede | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingChangesCount, setPendingChangesCount] = useState(0);
  const [recursosOcultos, setRecursosOcultos] = useState<{ tipos: Set<string>; marcas: Set<string>; modelos: Set<string> }>({
    tipos: new Set(),
    marcas: new Set(),
    modelos: new Set(),
  });
  const { toast } = useToast();
  const [isAdmin, setIsAdmin] = useState(false);

  // Update pending changes count
  useEffect(() => {
    if (perfil?.empresa_id) {
      const count = offlineStorage.getPendingChangesByEmpresa(perfil.empresa_id).length;
      setPendingChangesCount(count);
    }
  }, [perfil?.empresa_id, clientes, sistemas, elementos]);

  // NOTE: Offline data is now only cleared on explicit signOut
  // This prevents data loss on page reload or session restoration

  // Load user role
  useEffect(() => {
    const loadRole = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      const { data: perfilData } = await supabase
        .from('perfiles')
        .select('rol')
        .eq('user_id', user.id)
        .single();
      setIsAdmin(perfilData?.rol === 'admin' || perfilData?.rol === 'gestor');
    };
    loadRole();
  }, [user]);

  const cargarClientes = async () => {
    if (!perfil?.empresa_id) return;
    
    setIsLoading(true);
    try {
      if (!isOnline) {
        const offlineData = offlineStorage.loadOffline(perfil.empresa_id, 'clientes');
        if (offlineData) {
          setClientes(offlineData);
          sonnerToast.info('Cargando datos guardados localmente');
        }
        return;
      }

      const { data, error } = await supabase
        .from('clientes')
        .select('*')
        .order('nombre');
      // Note: we load ALL clients (active+inactive) so the UI can toggle visibility

      if (error) throw error;
      setClientes(data || []);
      
      if (data) {
        offlineStorage.saveOffline(perfil.empresa_id, 'clientes', data);
      }
    } catch (error: any) {
      console.error('Error loading clientes:', error);
      const offlineData = offlineStorage.loadOffline(perfil.empresa_id, 'clientes');
      if (offlineData) {
        setClientes(offlineData);
        sonnerToast.info('Cargando datos guardados localmente');
      } else {
        toast({
          title: "Error",
          description: "No se pudieron cargar los clientes.",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const cargarSistemas = async () => {
    if (!perfil?.empresa_id) return;
    
    setIsLoading(true);
    try {
      if (!isOnline) {
        const offlineData = offlineStorage.loadOffline(perfil.empresa_id, 'sistemas');
        if (offlineData) {
          setSistemas(offlineData);
        }
        return;
      }

      const { data, error } = await supabase
        .from('sistemas')
        .select('*, clientes!inner(nombre)')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const sistemasData = (data || []).map((sistema: any) => ({
        ...sistema,
        categoria: sistema.categoria as 'seguridad' | 'pci' || 'seguridad'
      }));
      
      setSistemas(sistemasData);
      
      if (data) {
        offlineStorage.saveOffline(perfil.empresa_id, 'sistemas', sistemasData);
      }
    } catch (error: any) {
      console.error('Error loading sistemas:', error);
      const offlineData = offlineStorage.loadOffline(perfil.empresa_id, 'sistemas');
      if (offlineData) {
        setSistemas(offlineData);
        sonnerToast.info('Cargando datos guardados localmente');
      } else {
        toast({
          title: "Error",
          description: "No se pudieron cargar los sistemas.",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const cargarElementos = async () => {
    if (!perfil?.empresa_id) return;
    
    setIsLoading(true);
    try {
      if (!isOnline) {
        const offlineData = offlineStorage.loadOffline(perfil.empresa_id, 'elementos');
        if (offlineData) {
          setElementos(offlineData);
        }
        return;
      }

      const { data, error } = await supabase
        .from('elementos')
        .select(`
          *,
          sistemas!inner(
            tipo,
            clientes!inner(nombre)
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setElementos(data || []);
      
      if (data) {
        offlineStorage.saveOffline(perfil.empresa_id, 'elementos', data);
      }
    } catch (error: any) {
      console.error('Error loading elementos:', error);
      const offlineData = offlineStorage.loadOffline(perfil.empresa_id, 'elementos');
      if (offlineData) {
        setElementos(offlineData);
        sonnerToast.info('Cargando datos guardados localmente');
      } else {
        toast({
          title: "Error",
          description: "No se pudieron cargar los elementos.",
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Sync pending changes
  const sincronizarCambiosPendientes = async () => {
    if (!perfil?.empresa_id || !isOnline) return;

    setIsSyncing(true);
    const changes = offlineStorage.getPendingChangesByEmpresa(perfil.empresa_id);
    let syncedCount = 0;
    let errorCount = 0;

    for (const change of changes) {
      try {
        if (change.tableName === 'clientes') {
          if (change.operation === 'insert') {
            await supabase.from('clientes').insert([change.data]);
          } else if (change.operation === 'update') {
            await supabase.from('clientes').update(change.data).eq('id', change.data.id);
          } else if (change.operation === 'delete') {
            await supabase.from('clientes').delete().eq('id', change.data.id);
          }
        } else if (change.tableName === 'sistemas') {
          if (change.operation === 'insert') {
            await supabase.from('sistemas').insert([change.data]);
          } else if (change.operation === 'update') {
            await supabase.from('sistemas').update(change.data).eq('id', change.data.id);
          } else if (change.operation === 'delete') {
            await supabase.from('sistemas').delete().eq('id', change.data.id);
          }
        } else if (change.tableName === 'elementos') {
          if (change.operation === 'insert') {
            await supabase.from('elementos').insert([change.data]);
          } else if (change.operation === 'update') {
            await supabase.from('elementos').update(change.data).eq('id', change.data.id);
          } else if (change.operation === 'delete') {
            await supabase.from('elementos').delete().eq('id', change.data.id);
          }
        }

        offlineStorage.removePendingChange(change.id);
        syncedCount++;
      } catch (error) {
        console.error('Error syncing change:', error);
        errorCount++;
      }
    }

    if (syncedCount > 0) {
      await Promise.all([cargarClientes(), cargarSistemas(), cargarElementos()]);
    }

    setIsSyncing(false);
    setPendingChangesCount(offlineStorage.getPendingChangesByEmpresa(perfil.empresa_id).length);

    if (errorCount > 0) {
      sonnerToast.warning(`Sincronizados ${syncedCount} cambios. ${errorCount} con errores.`);
    }
  };

  // CRUD Clientes with offline support
  const crearCliente = async (cliente: Omit<Cliente, 'id'>) => {
    if (!perfil?.empresa_id) return;

    try {
      const clienteData = {
        ...cliente,
        empresa_id: perfil.empresa_id
      };

      if (!isOnline) {
        offlineStorage.addPendingChange({
          empresaId: perfil.empresa_id,
          userId: user?.id || '',
          tableName: 'clientes',
          operation: 'insert',
          data: clienteData
        });
        sonnerToast.info('Cliente guardado. Se sincronizará cuando haya conexión.');
        setPendingChangesCount(prev => prev + 1);
        return;
      }

      const { data, error } = await supabase
        .from('clientes')
        .insert([clienteData])
        .select()
        .single();

      if (error) throw error;

      // Fire-and-forget Holded sync if integration active
      if (data?.id) {
        supabase.from('holded_integraciones')
          .select('activa').eq('empresa_id', perfil.empresa_id).maybeSingle()
          .then(({ data: integ }) => {
            if (integ?.activa) {
              supabase.functions.invoke('holded-sync-cliente', {
                body: { clienteId: data.id, accion: 'create' },
              }).catch(() => {});
            }
          });
      }

      await cargarClientes();
      toast({
        title: "Cliente creado",
        description: "El cliente se ha creado correctamente."
      });
    } catch (error: any) {
      console.error('Error creating cliente:', error);
      
      if (error.message?.includes('fetch')) {
        offlineStorage.addPendingChange({
          empresaId: perfil.empresa_id,
          userId: user?.id || '',
          tableName: 'clientes',
          operation: 'insert',
          data: { ...cliente, empresa_id: perfil.empresa_id }
        });
        sonnerToast.info('Cliente guardado localmente. Se sincronizará automáticamente.');
        setPendingChangesCount(prev => prev + 1);
      } else {
        toast({
          title: "Error",
          description: "No se pudo crear el cliente.",
          variant: "destructive"
        });
      }
    }
  };

  const editarCliente = async (id: string, cliente: Partial<Cliente>) => {
    if (!perfil?.empresa_id) return;

    try {
      if (!isOnline) {
        offlineStorage.addPendingChange({
          empresaId: perfil.empresa_id,
          userId: user?.id || '',
          tableName: 'clientes',
          operation: 'update',
          data: { id, ...cliente }
        });
        sonnerToast.info('Cambios guardados. Se sincronizarán cuando haya conexión.');
        setPendingChangesCount(prev => prev + 1);
        return;
      }

      const { data, error } = await supabase
        .from('clientes')
        .update(cliente)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Fire-and-forget Holded sync if integration active
      supabase.from('holded_integraciones')
        .select('activa').eq('empresa_id', perfil.empresa_id).maybeSingle()
        .then(({ data: integ }) => {
          if (integ?.activa) {
            supabase.functions.invoke('holded-sync-cliente', {
              body: { clienteId: id, accion: 'update' },
            }).catch(() => {});
          }
        });

      await cargarClientes();
      toast({
        title: "Cliente actualizado",
        description: "El cliente se ha actualizado correctamente."
      });
    } catch (error: any) {
      console.error('Error updating cliente:', error);
      
      if (error.message?.includes('fetch')) {
        offlineStorage.addPendingChange({
          empresaId: perfil.empresa_id,
          userId: user?.id || '',
          tableName: 'clientes',
          operation: 'update',
          data: { id, ...cliente }
        });
        sonnerToast.info('Cambios guardados localmente. Se sincronizarán automáticamente.');
        setPendingChangesCount(prev => prev + 1);
      } else {
        toast({
          title: "Error",
          description: "No se pudo actualizar el cliente.",
          variant: "destructive"
        });
      }
    }
  };

  const eliminarCliente = async (id: string) => {
    if (!perfil?.empresa_id) return;

    try {
      if (!isOnline) {
        offlineStorage.addPendingChange({
          empresaId: perfil.empresa_id,
          userId: user?.id || '',
          tableName: 'clientes',
          operation: 'update',
          data: { id, activo: false }
        });
        sonnerToast.info('Archivado guardado. Se sincronizará cuando haya conexión.');
        setPendingChangesCount(prev => prev + 1);
        return;
      }

      const { error } = await supabase
        .from('clientes')
        .update({ activo: false })
        .eq('id', id);

      if (error) throw error;

      await cargarClientes();
      toast({
        title: "Cliente archivado",
        description: "El cliente se ha archivado correctamente. Puedes restaurarlo en cualquier momento."
      });
    } catch (error: any) {
      console.error('Error deleting cliente:', error);
      
      if (error.message?.includes('fetch')) {
        offlineStorage.addPendingChange({
          empresaId: perfil.empresa_id,
          userId: user?.id || '',
          tableName: 'clientes',
          operation: 'update',
          data: { id, activo: false }
        });
        sonnerToast.info('Archivado guardado localmente. Se sincronizará automáticamente.');
        setPendingChangesCount(prev => prev + 1);
      } else {
        toast({
          title: "Error",
          description: "No se pudo eliminar el cliente.",
          variant: "destructive"
        });
      }
    }
  };

  // CRUD Sistemas (similar pattern)
  const crearSistema = async (sistema: Omit<Sistema, 'id'>) => {
    if (!perfil?.empresa_id) return;

    try {
      const sistemaData = {
        ...sistema,
        empresa_id: perfil.empresa_id
      };

      if (!isOnline) {
        offlineStorage.addPendingChange({
          empresaId: perfil.empresa_id,
          userId: user?.id || '',
          tableName: 'sistemas',
          operation: 'insert',
          data: sistemaData
        });
        sonnerToast.info('Sistema guardado. Se sincronizará cuando haya conexión.');
        setPendingChangesCount(prev => prev + 1);
        return;
      }

      const { data, error } = await supabase
        .from('sistemas')
        .insert([sistemaData])
        .select()
        .single();

      if (error) throw error;

      await cargarSistemas();
      toast({
        title: "Sistema creado",
        description: "El sistema se ha creado correctamente."
      });
    } catch (error: any) {
      console.error('Error creating sistema:', error);
      toast({
        title: "Error",
        description: "No se pudo crear el sistema.",
        variant: "destructive"
      });
    }
  };

  const editarSistema = async (id: string, sistema: Partial<Sistema>) => {
    try {
      const { data, error } = await supabase
        .from('sistemas')
        .update(sistema)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      await cargarSistemas();
      toast({
        title: "Sistema actualizado",
        description: "El sistema se ha actualizado correctamente."
      });
    } catch (error) {
      console.error('Error updating sistema:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el sistema.",
        variant: "destructive"
      });
    }
  };

  const archivarSistema = async (id: string) => {
    try {
      const payload: any = {
        activo: false,
        archivado_at: new Date().toISOString(),
        archivado_por: user?.id || null,
      };

      if (!isOnline && perfil?.empresa_id) {
        offlineStorage.addPendingChange({
          empresaId: perfil.empresa_id,
          userId: user?.id || '',
          tableName: 'sistemas',
          operation: 'update',
          data: { id, ...payload }
        });
        setSistemas(prev => prev.map(s => (s.id === id ? { ...s, ...payload } : s)));
        sonnerToast.info('Sistema archivado. Se sincronizará cuando haya conexión.');
        setPendingChangesCount(prev => prev + 1);
        return;
      }

      const { error } = await supabase
        .from('sistemas')
        .update(payload)
        .eq('id', id);

      if (error) throw error;

      await cargarSistemas();
      toast({
        title: "Sistema archivado",
        description: "El sistema y su inventario dejan de mostrarse, pero se conserva el histórico."
      });
    } catch (error) {
      console.error('Error archiving sistema:', error);
      toast({
        title: "Error",
        description: "No se pudo archivar el sistema.",
        variant: "destructive"
      });
    }
  };

  const restaurarSistema = async (id: string) => {
    try {
      const { error } = await supabase
        .from('sistemas')
        .update({ activo: true, archivado_at: null, archivado_por: null })
        .eq('id', id);

      if (error) throw error;

      await cargarSistemas();
      toast({
        title: "Sistema restaurado",
        description: "El sistema vuelve a estar activo."
      });
    } catch (error) {
      console.error('Error restoring sistema:', error);
      toast({
        title: "Error",
        description: "No se pudo restaurar el sistema.",
        variant: "destructive"
      });
    }
  };

  const eliminarSistemaDefinitivo = async (id: string): Promise<{ ok: boolean; motivo?: string }> => {
    try {
      const [checklists, informes, anomalias] = await Promise.all([
        supabase.from('checklists_seguridad').select('id', { count: 'exact', head: true }).eq('sistema_id', id),
        supabase.from('informes_consolidados').select('id', { count: 'exact', head: true }).contains('sistemas_ids', [id]),
        supabase.from('elementos').select('id', { count: 'exact', head: true }).eq('sistema_id', id),
      ]);

      const motivos: string[] = [];
      if ((checklists.count ?? 0) > 0) motivos.push('tiene revisiones/checklists');
      if (!informes.error && (informes.count ?? 0) > 0) motivos.push('aparece en informes consolidados');
      if ((anomalias.count ?? 0) > 0) motivos.push(`tiene ${anomalias.count} elementos de inventario`);

      if (motivos.length > 0) {
        const motivo = `No se puede borrar definitivamente porque ${motivos.join(' y ')}. Mantenlo archivado para conservar el histórico.`;
        toast({ title: "Borrado no permitido", description: motivo, variant: "destructive" });
        return { ok: false, motivo };
      }

      const { error } = await supabase
        .from('sistemas')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await cargarSistemas();
      toast({
        title: "Sistema eliminado",
        description: "El sistema se ha eliminado definitivamente."
      });
      return { ok: true };
    } catch (error) {
      console.error('Error deleting sistema:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el sistema.",
        variant: "destructive"
      });
      return { ok: false, motivo: 'Error inesperado' };
    }
  };

  // CRUD Elementos
  const crearElemento = async (elemento: Omit<Elemento, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('elementos')
        .insert([elemento])
        .select()
        .single();

      if (error) throw error;

      await cargarElementos();
      toast({
        title: "Elemento creado",
        description: "El elemento se ha creado correctamente."
      });
    } catch (error) {
      console.error('Error creating elemento:', error);
      toast({
        title: "Error",
        description: "No se pudo crear el elemento.",
        variant: "destructive"
      });
    }
  };

  const editarElemento = async (id: string, elemento: Partial<Elemento>) => {
    try {
      const { data, error } = await supabase
        .from('elementos')
        .update(elemento)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      await cargarElementos();
      toast({
        title: "Elemento actualizado",
        description: "El elemento se ha actualizado correctamente."
      });
    } catch (error) {
      console.error('Error updating elemento:', error);
      toast({
        title: "Error",
        description: "No se pudo actualizar el elemento.",
        variant: "destructive"
      });
    }
  };

  const eliminarElemento = async (id: string) => {
    try {
      const { error } = await supabase
        .from('elementos')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await cargarElementos();
      toast({
        title: "Elemento eliminado",
        description: "El elemento se ha eliminado correctamente."
      });
    } catch (error) {
      console.error('Error deleting elemento:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el elemento.",
        variant: "destructive"
      });
    }
  };

  // Funciones para tipos de equipo
  const sincronizarTiposEquipo = async () => {
    try {
      const { data, error } = await supabase
        .from('tipos_equipo')
        .select('*')
        .order('nombre');

      if (error) throw error;

      setTiposEquipo((data || []).map((tipo: any) => ({
        ...tipo,
        categoria: tipo.categoria as 'seguridad' | 'pci' || 'seguridad'
      })));
    } catch (error) {
      console.error('Error al sincronizar tipos de equipo:', error);
    }
  };

  const sincronizarMarcasEquipo = async () => {
    try {
      const { data, error } = await supabase
        .from('marcas_equipo')
        .select('*')
        .order('nombre');

      if (error) throw error;

      setMarcasEquipo((data || []).map((marca: any) => ({
        ...marca,
        categoria: marca.categoria as 'seguridad' | 'pci' || 'seguridad'
      })));
    } catch (error) {
      console.error('Error al sincronizar marcas de equipo:', error);
    }
  };

  const sincronizarModelosEquipo = async () => {
    try {
      const { data, error } = await supabase
        .from('modelos_equipo')
        .select('*')
        .order('nombre');

      if (error) throw error;

      setModelosEquipo((data || []).map((modelo: any) => ({
        ...modelo,
        categoria: modelo.categoria as 'seguridad' | 'pci' || 'seguridad'
      })));
    } catch (error) {
      console.error('Error al sincronizar modelos de equipo:', error);
    }
  };

  const crearTipoEquipo = async (nombre: string, sistemaTipo: string) => {
    try {
      const sistemaTipoNormalizado = sistemaTipo.toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      
      const { data, error } = await supabase
        .from('tipos_equipo')
        .insert([{ 
          nombre, 
          sistema_tipo: sistemaTipoNormalizado,
          empresa_id: perfil?.empresa_id 
        }])
        .select()
        .single();

      if (error) throw error;

      await sincronizarTiposEquipo();
      toast({
        title: "Tipo de equipo creado",
        description: "El tipo de equipo se ha creado correctamente."
      });
    } catch (error) {
      console.error('Error al crear tipo de equipo:', error);
      toast({
        title: "Error",
        description: "No se pudo crear el tipo de equipo.",
        variant: "destructive"
      });
    }
  };

  const crearMarcaEquipo = async (nombre: string, sistemaTipo: string) => {
    try {
      const sistemaTipoNormalizado = sistemaTipo.toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      
      const { data, error } = await supabase
        .from('marcas_equipo')
        .insert([{ 
          nombre, 
          sistema_tipo: sistemaTipoNormalizado,
          empresa_id: perfil?.empresa_id 
        }])
        .select()
        .single();

      if (error) throw error;

      await sincronizarMarcasEquipo();
      toast({
        title: "Marca creada",
        description: "La marca se ha creado correctamente."
      });
    } catch (error) {
      console.error('Error al crear marca:', error);
      toast({
        title: "Error",
        description: "No se pudo crear la marca.",
        variant: "destructive"
      });
    }
  };

  const crearModeloEquipo = async (nombre: string, marcaId: string, sistemaTipo: string, tipoEquipo?: string) => {
    try {
      const sistemaTipoNormalizado = sistemaTipo.toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
      
      const { data, error } = await supabase
        .from('modelos_equipo')
        .insert([{ 
          nombre, 
          marca_id: marcaId, 
          sistema_tipo: sistemaTipoNormalizado,
          tipo_equipo: tipoEquipo || null,
          empresa_id: perfil?.empresa_id 
        }])
        .select()
        .single();

      if (error) throw error;

      await sincronizarModelosEquipo();
      toast({
        title: "Modelo creado",
        description: "El modelo se ha creado correctamente."
      });
    } catch (error) {
      console.error('Error al crear modelo:', error);
      toast({
        title: "Error",
        description: "No se pudo crear el modelo.",
        variant: "destructive"
      });
    }
  };

  // NUEVO: Editar/Eliminar Tipos, Marcas, Modelos
  // Helper: ids de sistemas de la empresa que coinciden con un sistema_tipo
  const sistemasIdsPorTipo = async (sistemaTipo: string): Promise<string[]> => {
    const { data } = await supabase
      .from('sistemas')
      .select('id, tipo');
    return (data ?? [])
      .filter((s: any) => (s.tipo ?? '').toString().toLowerCase() === sistemaTipo.toLowerCase())
      .map((s: any) => s.id);
  };

  const editarTipoEquipo = async (id: string, cambios: Partial<TipoEquipo>) => {
    // Leemos el nombre anterior para poder propagar el rename a elementos.
    const { data: prev } = await supabase
      .from('tipos_equipo')
      .select('nombre, sistema_tipo')
      .eq('id', id)
      .maybeSingle();

    const updateData: Partial<TipoEquipo> = {};
    if (typeof cambios.nombre === 'string') updateData.nombre = cambios.nombre;
    if (typeof cambios.sistema_tipo === 'string') {
      updateData.sistema_tipo = cambios.sistema_tipo.toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    }

    // RLS solo permite actualizar elementos de la propia empresa
    const { data, error } = await supabase
      .from('tipos_equipo')
      .update(updateData)
      .eq('id', id)
      .select();

    if (error) {
      console.error('Error al editar tipo de equipo:', error);
      toast({
        title: "Error",
        description: "No se pudo editar el tipo de equipo.",
        variant: "destructive",
      });
      return;
    }

    // Si no se actualizó nada, es porque el elemento es global o de otra empresa
    if (!data || data.length === 0) {
      toast({
        title: "No permitido",
        description: "Solo puedes editar tipos de equipo de tu empresa. Los tipos globales no se pueden editar.",
        variant: "destructive",
      });
      return;
    }

    // Propagar rename a elementos y modelos que usaban el nombre antiguo
    if (prev?.nombre && updateData.nombre && prev.nombre !== updateData.nombre) {
      const sistemaTipoNorm = (prev.sistema_tipo || '').toLowerCase()
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '');

      if (sistemaTipoNorm) {
        const sistemaIds = await sistemasIdsPorTipo(sistemaTipoNorm);
        if (sistemaIds.length) {
          await supabase
            .from('elementos')
            .update({ tipo_equipo: updateData.nombre })
            .eq('tipo_equipo', prev.nombre)
            .in('sistema_id', sistemaIds);

          // También actualizar elementos.tipo (campo principal usado por el formulario)
          await supabase
            .from('elementos')
            .update({ tipo: updateData.nombre })
            .eq('tipo', prev.nombre)
            .in('sistema_id', sistemaIds);
        }
      }

      // Propagar al catálogo de modelos (solo los de la empresa; los globales no son editables).
      // Comparación case-insensitive para tolerar variaciones antiguas.
      if (perfil?.empresa_id) {
        await supabase
          .from('modelos_equipo')
          .update({ tipo_equipo: updateData.nombre })
          .ilike('tipo_equipo', prev.nombre)
          .eq('empresa_id', perfil.empresa_id);
      }
    }

    await sincronizarTiposEquipo();
    await sincronizarModelosEquipo();
    toast({
      title: "Tipo actualizado",
      description: "El tipo de equipo se ha actualizado correctamente.",
    });
  };


  const eliminarTipoEquipo = async (id: string) => {
    // RLS solo permite eliminar elementos de la propia empresa
    // Hacemos el delete y verificamos si se eliminó algo
    const { data, error } = await supabase
      .from('tipos_equipo')
      .delete()
      .eq('id', id)
      .select();

    if (error) {
      console.error('Error al eliminar tipo de equipo:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el tipo de equipo.",
        variant: "destructive",
      });
      return;
    }

    // Si no se eliminó nada, es porque el elemento es global o de otra empresa
    if (!data || data.length === 0) {
      toast({
        title: "No permitido",
        description: "Solo puedes eliminar tipos de equipo de tu empresa. Los tipos globales no se pueden eliminar.",
        variant: "destructive",
      });
      return;
    }

    await sincronizarTiposEquipo();
    toast({
      title: "Tipo eliminado",
      description: "El tipo de equipo se ha eliminado correctamente.",
    });
  };

  const editarMarcaEquipo = async (id: string, cambios: Partial<MarcaEquipo>) => {
    const { data: prev } = await supabase
      .from('marcas_equipo')
      .select('nombre, sistema_tipo')
      .eq('id', id)
      .maybeSingle();

    const updateData: Partial<MarcaEquipo> = {};
    if (typeof cambios.nombre === 'string') updateData.nombre = cambios.nombre;

    // RLS solo permite actualizar elementos de la propia empresa
    const { data, error } = await supabase
      .from('marcas_equipo')
      .update(updateData)
      .eq('id', id)
      .select();

    if (error) {
      console.error('Error al editar marca:', error);
      toast({
        title: "Error",
        description: "No se pudo editar la marca.",
        variant: "destructive",
      });
      return;
    }

    // Si no se actualizó nada, es porque el elemento es global o de otra empresa
    if (!data || data.length === 0) {
      toast({
        title: "No permitido",
        description: "Solo puedes editar marcas de tu empresa. Las marcas globales no se pueden editar.",
        variant: "destructive",
      });
      return;
    }

    // Propagar rename a elementos.marca (mismo sistema_tipo)
    if (prev?.nombre && updateData.nombre && prev.nombre !== updateData.nombre && prev.sistema_tipo) {
      const sistemaIds = await sistemasIdsPorTipo(prev.sistema_tipo);
      if (sistemaIds.length) {
        await supabase
          .from('elementos')
          .update({ marca: updateData.nombre })
          .eq('marca', prev.nombre)
          .in('sistema_id', sistemaIds);
      }
    }


    await sincronizarMarcasEquipo();
    toast({
      title: "Marca actualizada",
      description: "La marca se ha actualizado correctamente.",
    });
  };

  const eliminarMarcaEquipo = async (id: string) => {
    // RLS solo permite eliminar elementos de la propia empresa
    // Hacemos el delete y verificamos si se eliminó algo
    const { data, error } = await supabase
      .from('marcas_equipo')
      .delete()
      .eq('id', id)
      .select();

    if (error) {
      console.error('Error al eliminar marca:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la marca.",
        variant: "destructive",
      });
      return;
    }

    // Si no se eliminó nada, es porque el elemento es global o de otra empresa
    if (!data || data.length === 0) {
      toast({
        title: "No permitido",
        description: "Solo puedes eliminar marcas de tu empresa. Las marcas globales no se pueden eliminar.",
        variant: "destructive",
      });
      return;
    }

    await sincronizarMarcasEquipo();
    toast({
      title: "Marca eliminada",
      description: "La marca se ha eliminado correctamente.",
    });
  };

  const editarModeloEquipo = async (id: string, cambios: Partial<ModeloEquipo>) => {
    const { data: prev } = await supabase
      .from('modelos_equipo')
      .select('nombre, sistema_tipo, marca_id')
      .eq('id', id)
      .maybeSingle();

    const updateData: Partial<ModeloEquipo> = {};
    if (typeof cambios.nombre === 'string') updateData.nombre = cambios.nombre;
    if (typeof cambios.marca_id === 'string') updateData.marca_id = cambios.marca_id;

    // RLS solo permite actualizar elementos de la propia empresa
    const { data, error } = await supabase
      .from('modelos_equipo')
      .update(updateData)
      .eq('id', id)
      .select();

    if (error) {
      console.error('Error al editar modelo:', error);
      toast({
        title: "Error",
        description: "No se pudo editar el modelo.",
        variant: "destructive",
      });
      return;
    }

    // Si no se actualizó nada, es porque el elemento es global o de otra empresa
    if (!data || data.length === 0) {
      toast({
        title: "No permitido",
        description: "Solo puedes editar modelos de tu empresa. Los modelos globales no se pueden editar.",
        variant: "destructive",
      });
      return;
    }

    // Propagar rename a elementos.modelo (mismo sistema_tipo y marca)
    if (prev?.nombre && updateData.nombre && prev.nombre !== updateData.nombre && prev.sistema_tipo) {
      const sistemaIds = await sistemasIdsPorTipo(prev.sistema_tipo);
      const marcaIdRef = updateData.marca_id ?? prev.marca_id;
      const { data: marcaRow } = marcaIdRef
        ? await supabase.from('marcas_equipo').select('nombre').eq('id', marcaIdRef).maybeSingle()
        : { data: null };
      if (sistemaIds.length) {
        let q = supabase
          .from('elementos')
          .update({ modelo: updateData.nombre })
          .eq('modelo', prev.nombre)
          .in('sistema_id', sistemaIds);
        // Si conocemos la marca, restringimos para no pisar modelos homónimos de otras marcas
        if (marcaRow?.nombre) q = q.eq('marca', marcaRow.nombre);
        await q;
      }
    }


    await sincronizarModelosEquipo();
    toast({
      title: "Modelo actualizado",
      description: "El modelo se ha actualizado correctamente.",
    });
  };

  const eliminarModeloEquipo = async (id: string) => {
    // RLS solo permite eliminar elementos de la propia empresa
    // Hacemos el delete y verificamos si se eliminó algo
    const { data, error } = await supabase
      .from('modelos_equipo')
      .delete()
      .eq('id', id)
      .select();

    if (error) {
      console.error('Error al eliminar modelo:', error);
      toast({
        title: "Error",
        description: "No se pudo eliminar el modelo.",
        variant: "destructive",
      });
      return;
    }

    // Si no se eliminó nada, es porque el elemento es global o de otra empresa
    if (!data || data.length === 0) {
      toast({
        title: "No permitido",
        description: "Solo puedes eliminar modelos de tu empresa. Los modelos globales no se pueden eliminar.",
        variant: "destructive",
      });
      return;
    }

    await sincronizarModelosEquipo();
    toast({
      title: "Modelo eliminado",
      description: "El modelo se ha eliminado correctamente.",
    });
  };

  // ============== SEDES ==============
  const cargarSedes = async () => {
    if (!perfil?.empresa_id) return;
    try {
      const { data, error } = await supabase
        .from('cliente_sedes' as any)
        .select('*')
        .order('nombre');
      if (error) throw error;
      setSedes((data as any) || []);
    } catch (e) {
      console.error('Error cargando sedes:', e);
    }
  };

  const crearSede: SecurityContextType['crearSede'] = async (sede) => {
    if (!perfil?.empresa_id) return null;
    try {
      const payload = {
        ...sede,
        empresa_id: perfil.empresa_id,
        activa: sede.activa ?? true,
      };
      const { data, error } = await supabase
        .from('cliente_sedes' as any)
        .insert([payload])
        .select()
        .single();
      if (error) throw error;
      const nueva = data as unknown as Sede;
      setSedes(prev => [...prev, nueva].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      toast({ title: 'Sede creada', description: 'La sede se ha creado correctamente.' });
      return nueva;
    } catch (e) {
      console.error('Error creando sede:', e);
      toast({ title: 'Error', description: 'No se pudo crear la sede.', variant: 'destructive' });
      return null;
    }
  };

  const editarSede = async (id: string, cambios: Partial<Sede>) => {
    try {
      const { error } = await supabase
        .from('cliente_sedes' as any)
        .update(cambios as any)
        .eq('id', id);
      if (error) throw error;
      setSedes(prev => prev.map(s => s.id === id ? { ...s, ...cambios } as Sede : s));
      toast({ title: 'Sede actualizada' });
    } catch (e) {
      console.error('Error editando sede:', e);
      toast({ title: 'Error', description: 'No se pudo actualizar la sede.', variant: 'destructive' });
    }
  };

  const eliminarSede = async (id: string) => {
    try {
      // Comprobar si hay sistemas asignados
      const { count } = await supabase
        .from('sistemas')
        .select('id', { count: 'exact', head: true })
        .eq('sede_id', id as any);
      if ((count ?? 0) > 0) {
        // Soft delete: marcar inactiva
        await editarSede(id, { activa: false });
        toast({ title: 'Sede archivada', description: 'Tiene sistemas asociados, se ha marcado como inactiva.' });
        return;
      }
      const { error } = await supabase.from('cliente_sedes' as any).delete().eq('id', id);
      if (error) throw error;
      setSedes(prev => prev.filter(s => s.id !== id));
      toast({ title: 'Sede eliminada' });
    } catch (e) {
      console.error('Error eliminando sede:', e);
      toast({ title: 'Error', description: 'No se pudo eliminar la sede.', variant: 'destructive' });
    }
  };

  const sincronizarRecursosOcultos = async () => {
    if (!perfil?.empresa_id) return;
    const { data, error } = await supabase
      .from('catalogo_ocultos' as any)
      .select('recurso_tipo, recurso_id')
      .eq('empresa_id', perfil.empresa_id);
    if (error) {
      console.error('Error sincronizando catálogo ocultos:', error);
      return;
    }
    const tipos = new Set<string>();
    const marcas = new Set<string>();
    const modelos = new Set<string>();
    (data || []).forEach((row: any) => {
      if (row.recurso_tipo === 'tipo') tipos.add(row.recurso_id);
      else if (row.recurso_tipo === 'marca') marcas.add(row.recurso_id);
      else if (row.recurso_tipo === 'modelo') modelos.add(row.recurso_id);
    });
    setRecursosOcultos({ tipos, marcas, modelos });
  };

  const ocultarRecurso = async (recurso: 'tipo' | 'marca' | 'modelo', id: string) => {
    if (!perfil?.empresa_id) return;
    const { error } = await supabase
      .from('catalogo_ocultos' as any)
      .insert({ empresa_id: perfil.empresa_id, recurso_tipo: recurso, recurso_id: id });
    if (error && !String(error.message || '').includes('duplicate')) {
      console.error('Error al ocultar:', error);
      toast({ title: 'Error', description: 'No se pudo ocultar el elemento.', variant: 'destructive' });
      return;
    }
    setRecursosOcultos(prev => {
      const next = { tipos: new Set(prev.tipos), marcas: new Set(prev.marcas), modelos: new Set(prev.modelos) };
      if (recurso === 'tipo') next.tipos.add(id);
      else if (recurso === 'marca') next.marcas.add(id);
      else next.modelos.add(id);
      return next;
    });
  };

  const mostrarRecurso = async (recurso: 'tipo' | 'marca' | 'modelo', id: string) => {
    if (!perfil?.empresa_id) return;
    const { error } = await supabase
      .from('catalogo_ocultos' as any)
      .delete()
      .eq('empresa_id', perfil.empresa_id)
      .eq('recurso_tipo', recurso)
      .eq('recurso_id', id);
    if (error) {
      console.error('Error al mostrar:', error);
      toast({ title: 'Error', description: 'No se pudo mostrar el elemento.', variant: 'destructive' });
      return;
    }
    setRecursosOcultos(prev => {
      const next = { tipos: new Set(prev.tipos), marcas: new Set(prev.marcas), modelos: new Set(prev.modelos) };
      if (recurso === 'tipo') next.tipos.delete(id);
      else if (recurso === 'marca') next.marcas.delete(id);
      else next.modelos.delete(id);
      return next;
    });
  };

  // Load initial data
  useEffect(() => {
    if (perfil?.empresa_id) {
      Promise.all([
        cargarClientes(),
        cargarSistemas(),
        cargarElementos(),
        cargarSedes(),
        sincronizarTiposEquipo(),
        sincronizarMarcasEquipo(),
        sincronizarModelosEquipo(),
        sincronizarRecursosOcultos(),
      ]);
    }
  }, [perfil?.empresa_id]);

  return (
    <SecurityContext.Provider
      value={{
        clientes,
        sistemas,
        sistemasArchivados,
        elementos,
        tiposEquipo,
        marcasEquipo,
        modelosEquipo,
        sedes,
        clienteActual,
        sistemaActual,
        sedeActual,
        isSyncing,
        pendingChangesCount,
        setClienteActual,
        setSistemaActual,
        setSedeActual,
        cargarClientes,
        cargarSistemas,
        cargarElementos,
        cargarSedes,
        sincronizarCambiosPendientes,
        sincronizarTiposEquipo,
        sincronizarMarcasEquipo,
        sincronizarModelosEquipo,
        crearCliente,
        editarCliente,
        eliminarCliente,
        crearSistema,
        editarSistema,
        archivarSistema,
        restaurarSistema,
        eliminarSistemaDefinitivo,
        crearElemento,
        editarElemento,
        eliminarElemento,
        crearTipoEquipo,
        crearMarcaEquipo,
        crearModeloEquipo,
        editarTipoEquipo,
        eliminarTipoEquipo,
        editarMarcaEquipo,
        eliminarMarcaEquipo,
        editarModeloEquipo,
        eliminarModeloEquipo,
        recursosOcultos,
        ocultarRecurso,
        mostrarRecurso,
        crearSede,
        editarSede,
        eliminarSede,
        isLoading,
      }}
    >
      {children}
    </SecurityContext.Provider>
  );
};
